<template>
  <v-dialog v-model="dialog" max-width="500" persistent>
    <v-card>
      <v-sheet class="px-6 d-flex justify-space-between align-center" color="bg1">
        <v-card-title class="py-3 px-0">
          <v-icon class="mr-2">mdi-folder-move</v-icon>
          <span>Move "{{ movingItemName }}"</span>
        </v-card-title>
      </v-sheet>

      <v-card-text class="pt-4">
        <p class="text-body-2 mb-3">Select destination folder:</p>

        <div class="destination-tree-container">
          <v-treeview
            :items="destinationTree"
            :active.sync="selectedDestination"
            :open.sync="openNodes"
            activatable
            item-key="id"
            dense
            class="move-destination-tree">
            <template #prepend="{ item }">
              <v-icon small>
                {{ item.id === ROOT_ID ? 'mdi-folder-home' : 'mdi-folder' }}
              </v-icon>
            </template>
            <template #label="{ item }">
              <span :class="{ 'font-weight-medium': item.id === ROOT_ID }">
                {{ item.name }}
              </span>
            </template>
          </v-treeview>
        </div>
      </v-card-text>

      <v-card-actions style="background-color: var(--v-bg1-base)">
        <v-btn text color="error" @click="cancel">
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="confirm"
          :disabled="!hasValidSelection">
          Move
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const ROOT_ID = '__ROOT__';

export default {
  name: 'MoveProjectDialog',

  data() {
    return {
      ROOT_ID,
      dialog: false,
      movingItem: null,
      fullTree: [],
      selectedDestination: [],
      openNodes: [],
    };
  },

  computed: {
    movingItemName() {
      return this.movingItem?.name || '';
    },

    hasValidSelection() {
      return this.selectedDestination.length === 1;
    },

    selectedParentId() {
      if (!this.hasValidSelection) return undefined;
      const selected = this.selectedDestination[0];
      return selected === ROOT_ID ? null : selected;
    },

    destinationTree() {
      if (!this.movingItem || !this.fullTree.length) return [];

      const filtered = this.filterTree(this.fullTree, this.movingItem.id);
      return [{
        id: ROOT_ID,
        name: 'Root (top level)',
        children: filtered,
      }];
    },
  },

  methods: {
    /**
     * Open the move dialog.
     * @param {Object} item - The tree node being moved
     * @param {Array} treeData - The current normalized tree items
     */
    open(item, treeData) {
      this.movingItem = item;
      this.fullTree = treeData;
      this.selectedDestination = [];
      this.openNodes = [ROOT_ID];
      this.dialog = true;
    },

    /**
     * Recursively filter the tree to valid move destinations:
     * - Exclude the node being moved (and its entire subtree)
     * - Exclude leaf nodes that only contain images (not valid folder targets)
     */
    filterTree(nodes, excludeId) {
      return nodes
        .filter(n => n.id !== excludeId && !n.hasImages)
        .map(n => ({
          id: n.id,
          name: n.name,
          children: n.children ? this.filterTree(n.children, excludeId) : [],
        }));
    },

    confirm() {
      if (!this.hasValidSelection) return;

      this.$emit('confirm', {
        item: this.movingItem,
        newParentId: this.selectedParentId,
      });
      this.dialog = false;
    },

    cancel() {
      this.dialog = false;
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped lang="scss">
.destination-tree-container {
  max-height: 350px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px;

  .theme--light & {
    border-color: rgba(0, 0, 0, 0.12);
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.move-destination-tree {
  ::v-deep .v-treeview-node__root {
    min-height: 36px;
  }

  ::v-deep .v-treeview-node--active {
    background: rgba(var(--v-primary-base-rgb, 25, 118, 210), 0.15) !important;
  }
}
</style>
